import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUrlState } from '../../hooks/useUrlState';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { PageActions } from '../../components/layout/PageActions';
import { Chip } from '../../components/ui/Chip';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { DivisionTag } from '../../components/ui/DivisionTag';
import { EmptyState } from '../../components/ui/EmptyState';
import { fmtEur } from '../../lib/utils';
import type { Division } from '@masamune/shared';
import './reports.css';

interface DivisionReport { division: Division; revenue: number; cost?: number; marginPct?: number }
interface ClientTop { clientId: string; legalName: string; revenue: number; marginPct?: number }
interface Operational { projectsBelowMargin: number | null; clientsAtRisk: number; placementYTD: number; placementsTotal: number }
interface Monthly { month: number; total: number; year?: number }

// ─── Periodo: opzioni ────────────────────────────────────────────
type PeriodKey = 'ytd' | 'q1' | 'q2' | 'q3' | 'q4' | 'year' | 'all';
interface PeriodChoice { key: PeriodKey; year: number; label: string }

const PERIOD_KEYS: readonly PeriodKey[] = ['ytd', 'q1', 'q2', 'q3', 'q4', 'year', 'all'];

function isPeriodKey(v: string): v is PeriodKey {
  return (PERIOD_KEYS as readonly string[]).includes(v);
}

// Label umana per chiave+anno (vale anche per deep-link fuori dalla lista)
function periodLabel(key: PeriodKey, year: number): string {
  if (key === 'all') return 'Tutto lo storico';
  if (key === 'year') return `${year} (anno completo)`;
  if (key === 'ytd') return `YTD ${year}`;
  return `${key.toUpperCase()} ${year}`; // q1..q4
}

function buildPeriodOptions(): PeriodChoice[] {
  const now = new Date();
  const y = now.getFullYear();
  const py = y - 1;
  return [
    { key: 'ytd',  year: y,  label: periodLabel('ytd', y) },
    { key: 'q1',   year: y,  label: periodLabel('q1', y) },
    { key: 'q2',   year: y,  label: periodLabel('q2', y) },
    { key: 'q3',   year: y,  label: periodLabel('q3', y) },
    { key: 'q4',   year: y,  label: periodLabel('q4', y) },
    { key: 'year', year: py, label: periodLabel('year', py) },
    { key: 'ytd',  year: py, label: periodLabel('ytd', py) },
    { key: 'all',  year: y,  label: periodLabel('all', y) },
  ];
}

function periodQS(p: PeriodChoice): string {
  return `period=${p.key}&year=${p.year}`;
}

/**
 * Quali mesi dell'anno appartengono davvero al periodo scelto (indici 0-11).
 *
 * `/monthly` torna SEMPRE i dodici mesi dell'anno, anche con `period=q1`: serve
 * al grafico, che vuole la scala piena. La somma in testa alla pagina va quindi
 * ritagliata qui, altrimenti "Q1 2026" e "2026 (anno completo)" mostrano lo
 * stesso identico fatturato sotto l'etichetta "Periodo selezionato" — cioè il
 * numero dice una cosa e il selettore ne dice un'altra.
 */
function mesiDelPeriodo(p: PeriodChoice): number[] {
  const tutti = Array.from({ length: 12 }, (_, i) => i);
  if (p.key === 'q1') return tutti.slice(0, 3);
  if (p.key === 'q2') return tutti.slice(3, 6);
  if (p.key === 'q3') return tutti.slice(6, 9);
  if (p.key === 'q4') return tutti.slice(9, 12);
  if (p.key === 'ytd') {
    // YTD dell'anno in corso si ferma a questo mese; di un anno passato è tutto l'anno
    const ora = new Date();
    return p.year === ora.getFullYear() ? tutti.slice(0, ora.getMonth() + 1) : tutti;
  }
  return tutti; // year · all
}

// Etichetta su due righe per il period-picker (stesso linguaggio della dashboard)
function periodParts(p: PeriodChoice): { main: string; sub: string } {
  if (p.key === 'all') return { main: 'Tutto', sub: 'storico' };
  if (p.key === 'year') return { main: String(p.year), sub: 'anno completo' };
  return { main: p.key.toUpperCase(), sub: String(p.year) }; // ytd, q1..q4
}

// ─────────────────────────────────────────────────────────────────
export function ReportPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const showMargin = !!user?.role.canSeeMargin;

  // State periodo — sincronizzato con l'URL (?period=<key>&year=<YYYY>) così
  // il periodo è deep-linkabile e condivisibile con la tab Flusso della
  // Contabilità. Default (YTD anno corrente) = parametri assenti, URL pulito.
  const periodOptions = useMemo(() => buildPeriodOptions(), []);
  const currentYear = periodOptions[0]!.year;
  const [periodParam] = useUrlState<string>('period', 'ytd');
  const [yearParam] = useUrlState<string>('year', String(currentYear));
  const [, setParams] = useSearchParams();

  // Deriva la scelta validando i parametri (valori sconosciuti → default);
  // combinazioni valide ma fuori lista (es. deep-link da Flusso) restano usabili.
  const period = useMemo<PeriodChoice>(() => {
    const key = isPeriodKey(periodParam) ? periodParam : 'ytd';
    const parsed = Number.parseInt(yearParam, 10);
    const year = parsed >= 2000 && parsed <= 2100 ? parsed : currentYear;
    return (
      periodOptions.find((o) => o.key === key && o.year === year) ??
      { key, year, label: periodLabel(key, year) }
    );
  }, [periodParam, yearParam, periodOptions, currentYear]);

  // Scrive ENTRAMBE le chiavi in un'unica navigazione: due set separati di
  // useUrlState si sovrascriverebbero a vicenda nello stesso tick (il setter
  // parte dai param del render) e impilerebbero due entry di history per click.
  const setPeriod = useCallback(
    (next: PeriodChoice) => {
      setParams((prev) => {
        const sp = new URLSearchParams(prev);
        if (next.key === 'ytd') sp.delete('period'); else sp.set('period', next.key);
        if (next.year === currentYear) sp.delete('year'); else sp.set('year', String(next.year));
        return sp;
      });
    },
    [setParams, currentYear],
  );

  const [periodOpen, setPeriodOpen] = useState(false);
  const periodRef = useRef<HTMLDivElement>(null);

  // Close period dropdown click-outside
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (periodRef.current && !periodRef.current.contains(e.target as Node)) setPeriodOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Query keys includono il periodo → cache separata per periodo
  const qs = periodQS(period);
  const { data: divisions } = useQuery({
    queryKey: ['report', 'divs', period.key, period.year],
    queryFn: () => api.get<DivisionReport[]>(`/reports/divisions?${qs}`),
  });
  const { data: clientsTop } = useQuery({
    queryKey: ['report', 'clients', period.key, period.year],
    queryFn: () => api.get<ClientTop[]>(`/reports/clients-top?${qs}`),
  });
  const { data: monthly } = useQuery({
    queryKey: ['report', 'monthly', period.key, period.year],
    queryFn: () => api.get<Monthly[]>(`/reports/monthly?${qs}`),
  });
  // /operational rimane "stato corrente" → no period (non avrebbe senso)
  const { data: op } = useQuery({ queryKey: ['report', 'op'], queryFn: () => api.get<Operational>('/reports/operational') });

  // La scala delle barre è la divisione più grande del periodo, non un numero
  // scritto a mano. Qui c'era un pavimento di 420.000: col fatturato vero di
  // questo periodo (12.500 la divisione più alta) ogni barra usciva lunga il 3%
  // del binario, cioè il grafico non mostrava più niente. L'1 serve solo a non
  // dividere per zero quando il periodo è vuoto.
  const maxRev = Math.max(1, ...((divisions ?? []).map((d) => d.revenue ?? 0)));
  const monthlyMax = Math.max(1, ...((monthly ?? []).map((m) => m.total ?? 0)));

  // KPI riepilogo: somma dei soli mesi che stanno nel periodo scelto (vedi
  // mesiDelPeriodo) + best division / top client
  const mesiInclusi = useMemo(() => new Set(mesiDelPeriodo(period)), [period]);
  const periodRevenue = (monthly ?? []).reduce((a, m) => (mesiInclusi.has(m.month - 1) ? a + (m.total ?? 0) : a), 0);
  const deltaFatturato = period.key === 'all'
    // Lo storico completo non c'è: /monthly copre un anno per volta, e dirlo è
    // meglio che far credere che questo numero sia il fatturato di sempre.
    ? `solo ${period.year}`
    : `somma di ${mesiInclusi.size} mes${mesiInclusi.size === 1 ? 'e' : 'i'}`;
  const bestDivision = (divisions ?? []).reduce<DivisionReport | null>(
    (best, d) => (!best || (d.revenue ?? 0) > (best.revenue ?? 0) ? d : best),
    null,
  );
  const topClient = (clientsTop ?? [])[0];

  const isCurrentPeriod = (opt: PeriodChoice) => opt.key === period.key && opt.year === period.year;

  return (
    <div className="page">
      {/* ── RIFACIMENTO EDITORIALE: titolo grande e leggero, hairline aurora,
            numeri NUDI sul foglio (stat-band per le due sezioni KPI) ── */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Report & analytics</h1>
          {/* Una sola riga di sottotitolo; il periodo vive SOLO nel picker a destra */}
          <div className="ed-sub">Ricavi, marginalità e clienti per periodo.</div>
        </div>
        {/* Sul telefono questi comandi salgono nella barra in alto (PageActions):
            qui sotto il selettore di periodo si prendeva una riga di 60px
            sopra il primo numero. Sul computer non cambia niente. */}
        <PageActions className="flex gap-2 pb-1">
          {/* ── Period picker unico (linguaggio .period-picker della dashboard) ── */}
          <div ref={periodRef} className="relative">
            <button
              type="button"
              className="btn"
              onClick={() => setPeriodOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={periodOpen}
            >
              <Icon name="calendar" size={13} /> {period.label} <Icon name="chevronD" size={10} />
            </button>
            {periodOpen && (
              <div className="period-picker">
                <div className="period-picker-hd">
                  <span className="t-micro">Periodo</span>
                  <span className="mute text-[12px]">trimestre · anno · storico</span>
                </div>
                <div className="period-picker-body">
                  {periodOptions.map((opt) => {
                    const active = isCurrentPeriod(opt);
                    const parts = periodParts(opt);
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        className={`period-picker-item${active ? ' is-active' : ''}`}
                        onClick={() => { setPeriod(opt); setPeriodOpen(false); }}
                      >
                        <span className="period-picker-q">{parts.main}</span>
                        <span className="period-picker-y">{parts.sub}</span>
                        {active && <Icon name="check" size={12} className="period-picker-check" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <DesktopOnly
            cosa="L'esportazione del report"
            perche="genera un documento da archiviare o condividere; sul telefono restano tutti i numeri e i periodi da consultare."
          >
            <button className="btn" onClick={() => openModal('export', { what: 'Report', period: { key: period.key, year: period.year, label: period.label } })}>
              <Icon name="export" /> Esporta
            </button>
          </DesktopOnly>
        </PageActions>
      </div>

      {/* ── Banda riepilogo: dipende dal periodo selezionato — numeri nudi sul foglio ──
            mb-3.5 sui kicker = i 14px di .data-hd, che è lo stesso ruolo.
            Le fasce tengono il loro mb di .stat-band (10px): prima una aveva
            mb-3 e l'altra mb-5, e due fasce identiche sulla stessa pagina
            stavano a due distanze diverse dal blocco successivo. */}
      <div className="t-micro mb-3.5">Periodo selezionato</div>
      <div className="stat-band">
        <div>
          <div className="st-label">Fatturato</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={periodRevenue} format={fmtEur} /></div>
          <div className="st-delta">{deltaFatturato}</div>
        </div>
        <div>
          <div className="st-label">Miglior divisione</div>
          <div className="st-value">{bestDivision ? <DivisionTag division={bestDivision.division} /> : '—'}</div>
          {bestDivision && <div className="st-delta">€ {fmtEur(bestDivision.revenue)}</div>}
        </div>
        <div>
          <div className="st-label">Top cliente</div>
          <div className="st-value truncate">{topClient?.legalName ?? '—'}</div>
          {topClient && <div className="st-delta">€ {fmtEur(topClient.revenue)}</div>}
        </div>
      </div>

      {/* ── Banda operativa: stato corrente, indipendente dal periodo (lo dice già il kicker) ── */}
      <div className="t-micro mb-3.5">Operativo · oggi</div>
      <div className="stat-band">
        <div>
          <div className="st-label">Progetti sotto soglia</div>
          {/* Gating canSeeMargin: il backend manda null se il margine è riservato */}
          {op != null && op.projectsBelowMargin == null ? (
            <>
              <div className="st-value"><span className="mute-2">•••</span></div>
              <div className="st-delta">Riservato</div>
            </>
          ) : (
            <>
              <div className="st-value"><AnimatedNumber value={op?.projectsBelowMargin ?? 0} /></div>
              <div className="st-delta">margine basso</div>
            </>
          )}
        </div>
        <div>
          <div className="st-label">Clienti a rischio</div>
          <div className="st-value"><AnimatedNumber value={op?.clientsAtRisk ?? 0} /></div>
          <div className="st-delta">in pausa</div>
        </div>
        <div>
          <div className="st-label">Placement Talent</div>
          <div className="st-value"><AnimatedNumber value={op?.placementYTD ?? 0} /> <span className="u">/</span> <AnimatedNumber value={op?.placementsTotal ?? 0} /></div>
          <div className="st-delta">assunti / pipeline</div>
        </div>
      </div>

      <div className="t-micro mb-3.5">Marginalità</div>
      {/* report-bento: le colonne stanno in reports.css, non in uno style inline,
          perché sul telefono devono diventare una sola — e uno style inline non
          lo batte nessuna media query. */}
      <div className="report-bento mb-8">
        <div className="card">
          <div className="card-hd">
            <div className="flex items-center gap-3">
              <div className="icon-tile sm"><Icon name="euro" size={14} /></div>
              <div>
                <div className="t-h3">Margine per divisione</div>
                <div className="mute t-small">Ricavi e marginalità per area</div>
              </div>
            </div>
          </div>
          <div className="card-bd">
            {(divisions ?? []).length === 0 && (
              <EmptyState
                icon="chart"
                title="Nessun dato nel periodo"
                description="Cambia periodo per vedere ricavi e margini per divisione."
              />
            )}
            {(divisions ?? []).map((d) => {
              const w = maxRev > 0 ? ((d.revenue ?? 0) / maxRev) * 100 : 0;
              return (
                <div key={d.division} className="py-2.5">
                  <div className="row between mb-1.5">
                    <DivisionTag division={d.division} />
                    <span className="mono text-[12px] mute">€ {fmtEur(d.revenue)}</span>
                  </div>
                  <div className="grid items-center gap-2.5" style={{ gridTemplateColumns: '1fr 70px' }}>
                    <div className="barbar">
                      <span style={{ width: `${w}%` }} />
                    </div>
                    <span className={`mono text-right text-[12px] ${d.marginPct != null ? 'mute' : 'mute-2'}`}>
                      {d.marginPct != null ? `${d.marginPct.toFixed(1)}%` : '•••'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <div className="card-hd">
            <div className="flex items-center gap-3">
              <div className="icon-tile sm"><Icon name="users" size={14} /></div>
              <div>
                <div className="t-h3">Clienti più profittevoli</div>
                <div className="mute t-small">Per ricavi nel periodo</div>
              </div>
            </div>
            {/* Calcolato, non scritto: l'endpoint prende i primi cinque, ma se i
                clienti fatturati sono tre l'intestazione diceva lo stesso "Top 5". */}
            {!!(clientsTop ?? []).length && <span className="mute mono text-[12px]">Top {(clientsTop ?? []).length}</span>}
          </div>
          {/* tbl--card: sotto i 768px ogni riga diventa una scheda. Sul computer
              non fa niente. Regole: os-planning/mobile.md */}
          <table className="tbl tbl--card">
            <thead><tr><th>Cliente</th><th className="num" style={{ width: 120 }}>Ricavi</th>{showMargin && <th className="num" style={{ width: 96 }}>Margine</th>}<th className="num" style={{ width: 130 }}>Quota</th></tr></thead>
            <tbody>
              {(clientsTop ?? []).map((c) => {
                const totalTop = (clientsTop ?? []).reduce((a, x) => a + (x.revenue ?? 0), 0) || 1;
                const share = ((c.revenue ?? 0) / totalTop) * 100;
                return (
                  <tr key={c.clientId} className="cursor-pointer" onClick={() => navigate(`/clients/${c.clientId}`)}>
                    <td data-m="hd" className="font-semibold">{c.legalName}</td>
                    {/* nowrap: senza, in una colonna stretta il simbolo € finiva
                        su una riga e la cifra su quella dopo */}
                    <td data-m="key" data-l="Ricavi" className="num" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(c.revenue)}</td>
                    {showMargin && <td data-m="key" data-l="Margine" className="num"><Chip tone={(c.marginPct ?? 0) < 30 ? 'warn' : 'ok'}>{(c.marginPct ?? 0).toFixed(1)}%</Chip></td>}
                    <td data-m="key" data-l="Quota" className="num">
                      <div className="flex items-center gap-2 justify-end">
                        <div className="barbar" style={{ width: 56 }}><span style={{ width: `${share}%` }} /></div>
                        <span className="mono mute-2 text-[12px]" style={{ width: 38, textAlign: 'right' }}>{share.toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(clientsTop ?? []).length === 0 && (
                <tr>
                  <td colSpan={showMargin ? 4 : 3} style={{ padding: 0 }}>
                    <EmptyState
                      icon="users"
                      title="Nessun cliente fatturato"
                      description="Nessuna fattura emessa nel periodo selezionato."
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Niente kicker qui: duplicherebbe il titolo della card sottostante.
          Niente mb sull'ultimo blocco: i 32px si sommavano ai 64 di fondo
          pagina e lasciavano 96px di bianco dopo il grafico. */}
      <div className="card">
        <div className="card-hd">
          <div className="flex items-center gap-3">
            <div className="icon-tile sm"><Icon name="chart" size={14} /></div>
            <div>
              <div className="t-h3">Fatturato mensile {monthly?.[0]?.year ?? period.year}</div>
              <div className="mute t-small">Andamento mese per mese</div>
            </div>
          </div>
        </div>
        <div className="card-bd">
          <div className="grid items-end gap-1 h-[140px]" style={{ gridTemplateColumns: 'repeat(12, 1fr)' }}>
            {(monthly ?? Array.from({ length: 12 }, (_, i) => ({ month: i + 1, total: 0 }))).map((m, i) => {
              const isCurrentMonth = (monthly?.[0]?.year ?? period.year) === new Date().getFullYear() && i === new Date().getMonth();
              const pct = monthlyMax > 0 ? ((m.total ?? 0) / monthlyMax) * 100 : 0;
              return (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <div
                    className="w-full rounded-t-[3px]"
                    style={{
                      height: `${pct}%`,
                      // Mese corrente in accento viola, gli altri in ink desaturato
                      background: !m.total
                        ? 'var(--line)'
                        : isCurrentMonth
                          ? 'var(--accent)'
                          : 'var(--ink)',
                      opacity: m.total ? (isCurrentMonth ? 1 : 0.55) : 0.4,
                      transition: 'height var(--dur-slower) var(--ease-out)',
                    }}
                    title={`${['gen','feb','mar','apr','mag','giu','lug','ago','set','ott','nov','dic'][i]}: € ${fmtEur(m.total)}`}
                  />
                  <div className={`mono text-[12px] ${isCurrentMonth ? 'text-ink font-semibold' : 'mute'}`}>{['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'][i]}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

    </div>
  );
}
